import "@/App.css";
import "./i18n";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { Toaster } from "sonner";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import axios from "axios";
import Navbar from "./components/Navbar";
import Home from "./pages/Home";
import Login from "./pages/Login";
import Register from "./pages/Register";
import QuoteCalculator from "./pages/QuoteCalculator";
import CustomerDashboard from "./pages/CustomerDashboard";
import EmployeeDashboard from "./pages/EmployeeDashboard";
import PackageSpecs from "./pages/PackageSpecs";
import FloorPlans from "./pages/FloorPlans";
import FreeEstimate from "./pages/FreeEstimate";
import AboutUs from "./pages/AboutUs";
import SoCalRebuilds from "./pages/SoCalRebuilds";
import SingleFamilyHomes, { CatalogList, CatalogHomeDetail } from "./pages/SingleFamilyHomes";
import SfhHomes from "./pages/SfhHomes";
import Services from "./pages/Services";
import DesignMyHome from "./pages/DesignMyHome";
import InternalQuote from "./pages/InternalQuote";
import ProductDetail from "./pages/ProductDetail";
import AdminDashboard from "./pages/AdminDashboard";
import UserProfile from "./pages/UserProfile";
import Portfolio from "./pages/Portfolio";
import ADURegulations, { ADURegulationDetail } from "./pages/ADURegulations";
import PreApproved, { PreApprovedDetail } from "./pages/PreApproved";
import TurnkeyServices from "./pages/TurnkeyServices";
import News from "./pages/News";
import ThankYou from "./pages/ThankYou";
import AgentPartnership from "./pages/AgentPartnership";
import Events, { EventDetail, EventRegister } from "./pages/Events";
import Shop, { ShopCart, PackageDetail, ProductDetail as ShopProductDetail } from "./pages/Shop";
import AssignmentSelect from "./pages/AssignmentSelect";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import TermsAndConditions from "./pages/TermsAndConditions";
import ContactWidget from "./components/ContactWidget";
import Chatbot from "./components/Chatbot";
import { ImageViewer, initImageViewer } from "./components/ImageViewer";
import BlogList from "./pages/BlogList";
import BlogPost from "./pages/BlogPost";

// Protected Route Component
const ProtectedRoute = ({ children, requireEmployee = false }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-red-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (requireEmployee && user.role !== 'employee' && user.role !== 'admin') {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

// Chinese language wrapper — sets i18n to zh when entering /zh/* routes
function ZhWrapper({ children }) {
  const { i18n } = useTranslation();
  useEffect(() => {
    if (i18n.language !== 'zh') {
      i18n.changeLanguage('zh');
      localStorage.setItem('aligcus_lang', 'zh');
    }
  }, []);
  return children;
}

function AppRoutes() {
  return (
    <Routes>
      <Route path="/" element={<Home />} />
      {/* Chinese /zh/ routes — same components, auto-switch to Chinese */}
      <Route path="/zh" element={<ZhWrapper><Home /></ZhWrapper>} />
      <Route path="/zh/about" element={<ZhWrapper><AboutUs /></ZhWrapper>} />
      <Route path="/zh/agent-partnership" element={<ZhWrapper><AgentPartnership /></ZhWrapper>} />
      <Route path="/zh/portfolio" element={<ZhWrapper><Portfolio /></ZhWrapper>} />
      <Route path="/zh/thank-you" element={<ZhWrapper><ThankYou /></ZhWrapper>} />
      <Route path="/zh/quote" element={<ZhWrapper><QuoteCalculator /></ZhWrapper>} />
      <Route path="/zh/packages" element={<ZhWrapper><PackageSpecs /></ZhWrapper>} />
      <Route path="/zh/floor-plans" element={<ZhWrapper><FloorPlans /></ZhWrapper>} />
      <Route path="/zh/estimate" element={<ZhWrapper><FreeEstimate /></ZhWrapper>} />
      <Route path="/zh/services" element={<ZhWrapper><Services /></ZhWrapper>} />
      <Route path="/zh/design" element={<ZhWrapper><DesignMyHome /></ZhWrapper>} />
      <Route path="/zh/news" element={<ZhWrapper><News /></ZhWrapper>} />
      <Route path="/zh/events/south" element={<ZhWrapper><Events /></ZhWrapper>} />
      <Route path="/zh/events/north" element={<ZhWrapper><Events /></ZhWrapper>} />
      <Route path="/zh/events/detail/:id" element={<ZhWrapper><EventDetail /></ZhWrapper>} />
      <Route path="/zh/shop" element={<ZhWrapper><Shop /></ZhWrapper>} />
      <Route path="/zh/adu-regulations" element={<ZhWrapper><ADURegulations /></ZhWrapper>} />
      <Route path="/zh/adu-regulations/:id" element={<ZhWrapper><ADURegulationDetail /></ZhWrapper>} />
      <Route path="/zh/pre-approved" element={<ZhWrapper><PreApproved /></ZhWrapper>} />
      <Route path="/zh/pre-approved/:id" element={<ZhWrapper><PreApprovedDetail /></ZhWrapper>} />
      <Route path="/zh/*" element={<ZhWrapper><Home /></ZhWrapper>} />
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route path="/quote" element={<QuoteCalculator />} />
      <Route path="/packages" element={<PackageSpecs />} />
      <Route path="/floor-plans" element={<FloorPlans />} />
      <Route path="/estimate" element={<FreeEstimate />} />
      <Route path="/about" element={<AboutUs />} />
      <Route path="/agent-partnership" element={<AgentPartnership />} />
      <Route path="/socal-rebuilds" element={<SoCalRebuilds />} />
      <Route path="/single-family-homes" element={<SingleFamilyHomes />} />
      <Route path="/single-family-homes/:locationId" element={<CatalogList />} />
      <Route path="/single-family-homes/:locationId/:homeId" element={<CatalogHomeDetail />} />
      <Route path="/sf-homes" element={<SfhHomes />} />
      <Route path="/zh/sf-homes" element={<ZhWrapper><SfhHomes /></ZhWrapper>} />
      <Route path="/services" element={<Services />} />
      <Route path="/design" element={<DesignMyHome />} />
      <Route path="/products/:slug" element={<ProductDetail />} />
      <Route path="/internal" element={<InternalQuote />} />
      <Route path="/admin-dashboard" element={<AdminDashboard />} />
      <Route path="/profile" element={<ProfileRouter />} />
      <Route path="/user-profile" element={<UserProfile />} />
      <Route path="/portfolio" element={<Portfolio />} />
      <Route path="/thank-you" element={<ThankYou />} />
      <Route path="/adu-regulations" element={<ADURegulations />} />
      <Route path="/adu-regulations/:id" element={<ADURegulationDetail />} />
      <Route path="/pre-approved" element={<PreApproved />} />
      <Route path="/pre-approved/:id" element={<PreApprovedDetail />} />
      <Route path="/shop" element={<Shop />} />
      <Route path="/shop/cart" element={<ShopCart />} />
      <Route path="/shop/product/:id" element={<ShopProductDetail />} />
      <Route path="/shop/package/:id" element={<PackageDetail />} />
      <Route path="/shop/assign/:id" element={<AssignmentSelect />} />
      <Route path="/privacy-policy" element={<PrivacyPolicy />} />
      <Route path="/terms-and-conditions" element={<TermsAndConditions />} />
      <Route path="/turnkey-services" element={<TurnkeyServices />} />
      <Route path="/news" element={<News />} />
      <Route path="/blog" element={<BlogList />} />
      <Route path="/blog/:slug" element={<BlogPost />} />
      <Route path="/zh/blog" element={<ZhWrapper><BlogList /></ZhWrapper>} />
      <Route path="/zh/blog/:slug" element={<ZhWrapper><BlogPost /></ZhWrapper>} />
      <Route path="/events/south" element={<Events />} />
      <Route path="/events/north" element={<Events />} />
      <Route path="/events/detail/:id" element={<EventDetail />} />
      <Route path="/events/detail/:id/register" element={<EventRegister />} />
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <DashboardRouter />
          </ProtectedRoute>
        }
      />
      <Route
        path="/employee"
        element={
          <ProtectedRoute requireEmployee={true}>
            <EmployeeDashboard />
          </ProtectedRoute>
        }
      />
      {/* Google Ads landing page aliases — redirect to relevant content while preserving gclid & UTM */}
      <Route path="/600sqft" element={<PreserveSearchRedirect to="/floor-plans" />} />
      <Route path="/zh/600sqft" element={<PreserveSearchRedirect to="/zh/floor-plans" />} />
      {/* Catch-all 404: redirect to homepage preserving query strings (gclid for Google Ads tracking) */}
      <Route path="*" element={<PreserveSearchRedirect to="/" />} />
    </Routes>
  );
}

// Redirect that keeps the original ?query string so Google Ads gclid / UTM trackers still work.
function PreserveSearchRedirect({ to }) {
  const location = useLocation();
  return <Navigate to={`${to}${location.search}${location.hash}`} replace />;
}

// Dashboard Router - directs to appropriate dashboard based on role
function DashboardRouter() {
  const { user } = useAuth();
  
  if (user?.role === 'employee' || user?.role === 'admin') {
    return <Navigate to="/employee" replace />;
  }
  
  return <CustomerDashboard />;
}

// Profile Router - directs to appropriate page based on role
function ProfileRouter() {
  const role = localStorage.getItem('admin_role');
  const token = localStorage.getItem('admin_token');
  
  if (token && role === 'admin') {
    return <Navigate to="/admin-dashboard" replace />;
  }
  if (token && role === 'customer') {
    return <Navigate to="/user-profile" replace />;
  }
  // Not logged in - show login screen
  return <AdminDashboard />;
}

function App() {
  useEffect(() => { initImageViewer(); }, []);
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <Toaster position="top-right" richColors />
          <VisitorTracker />
          <ScrollToTop />
          <Navbar />
          <AppRoutes />
          <ContactWidget />
          <Chatbot />
          <ImageViewer />
        </BrowserRouter>
      </AuthProvider>
    </div>
  );
}

// Track page visits
function VisitorTracker() {
  const location = useLocation();
  useEffect(() => {
    const track = async () => {
      try {
        const email = localStorage.getItem('admin_email') || '';
        const role = localStorage.getItem('admin_role') || '';
        await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/admin/track`, {
          ip: 'client', path: location.pathname, user_agent: navigator.userAgent, referrer: document.referrer
        });
        await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/admin/access-log`, {
          ip: 'client', path: location.pathname, user_agent: navigator.userAgent, referrer: document.referrer,
          login_email: email, login_role: role, click_event: 'page_view'
        }).catch(() => {});
      } catch {}
    };
    track();
  }, [location.pathname]);
  return null;
}

// Scroll to top on route change (except hash links and specific paths)
function ScrollToTop() {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    // Don't scroll for hash links (e.g. /#portfolio, /services#design)
    if (hash) return;
    // Don't scroll for portfolio (keeps position)
    if (pathname === '/' && hash === '#portfolio') return;
    window.scrollTo(0, 0);
  }, [pathname, hash]);
  return null;
}

export default App;
